// ============================================================================
// DrillableCell — reusable cell wrapper for drill-down-eligible values in
// report grids. Today it renders styled text. Tomorrow, adding an onClick
// handler on the report side (or a `to` route prop here) will make the cell
// navigate — no other changes needed at the call site.
//
// Usage:
//   <DrillableCell>{voucherNo}</DrillableCell>                  // static (today)
//   <DrillableCell onClick={() => openVoucher(id)}>...</...>    // drill-down (future)
//
// Convention: cells that MIGHT become drillable in the future should already
// wrap in this component even when no onClick is provided. That way when
// drill-down lands we only add handlers — never restyle.
// ============================================================================

import React from 'react';

export interface DrillableCellProps {
  children: React.ReactNode;
  /** When provided, the cell renders as a clickable link with a hover cursor. */
  onClick?: () => void;
  /** Optional tooltip. */
  title?: string;
}

const DrillableCell: React.FC<DrillableCellProps> = ({ children, onClick, title }) => {
  const isDrillable = typeof onClick === 'function';

  return (
    <span
      onClick={onClick}
      role={isDrillable ? 'button' : undefined}
      tabIndex={isDrillable ? 0 : undefined}
      onKeyDown={isDrillable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick!();
        }
      } : undefined}
      title={title}
      style={{
        // When drillable: primary color + underline on hover + pointer cursor.
        // When static: normal text — but keep the same base styles so future
        // toggling doesn't shift the layout.
        color: isDrillable ? 'var(--primary-color, #2563eb)' : 'inherit',
        cursor: isDrillable ? 'pointer' : 'default',
        textDecoration: 'none',
        fontWeight: 'inherit',
        userSelect: isDrillable ? 'none' : 'text',
      }}
      onMouseEnter={isDrillable ? (e) => (e.currentTarget.style.textDecoration = 'underline') : undefined}
      onMouseLeave={isDrillable ? (e) => (e.currentTarget.style.textDecoration = 'none') : undefined}
    >
      {children}
    </span>
  );
};

export default DrillableCell;
